/**
 * The cold-start splash (concept A «The day fills in», D-86).
 *
 * index.html paints #splash, plain terracotta with the mark hidden, before any
 * JavaScript loads, and only on a cold start of the iPhone app, the Android app
 * or the home-screen app. A normal browser tab removes it at once. Here the mark
 * plays its entrance (the .play rules in mockup.css), holds still until the
 * first screen is ready, and fades away.
 *
 * It leaves at the latest of: the entrance finishing, MIN_SHOW_MS, and
 * splashReady(). SAFETY_MS lets the screen show its own error state when the
 * server never answers.
 */
import { useEffect } from 'react';

export const ENTRANCE_MS = 940; // frame 0-400 ms, bars 400-940 ms (mockup screen S, softer timing)
export const MIN_SHOW_MS = 800;
export const EXIT_MS = 400;
/** Proposed 2026-10-06, a guess rather than a measurement: change it here. */
export const SAFETY_MS = 6000;

export interface Splash {
  ready(): void;
}

export function createSplash(el: HTMLElement | null, now: () => number = () => performance.now()): Splash {
  if (!el || el.hidden) {
    el?.remove();
    return { ready() {} };
  }
  const started = now();
  let left = false;
  let waiting: ReturnType<typeof setTimeout> | undefined;

  const leave = () => {
    if (left) return;
    left = true;
    clearTimeout(waiting);
    clearTimeout(safety);
    el.classList.add('out');
    // Read on the test phones (doc 06): how long the splash covered the screen.
    console.info(`[splash] on screen ${Math.round(now() - started + EXIT_MS)} ms`);
    setTimeout(() => el.remove(), EXIT_MS);
  };
  const safety = setTimeout(leave, SAFETY_MS);
  el.classList.add('play');

  return {
    ready() {
      if (left || waiting !== undefined) return;
      const floor = Math.max(ENTRANCE_MS, MIN_SHOW_MS);
      waiting = setTimeout(leave, Math.max(0, floor - (now() - started)));
    },
  };
}

let current: Splash = { ready() {} };

/** Called once from main.tsx, before the app renders. */
export function startSplash(): void {
  current = createSplash(document.getElementById('splash'));
}

export function splashReady(): void {
  current.ready();
}

/** The first screen calls this; the splash leaves once `isReady` is true. */
export function useSplashReady(isReady: boolean): void {
  useEffect(() => {
    if (isReady) splashReady();
  }, [isReady]);
}
